import { Platform } from 'react-native';
import { supabase } from '../lib/supabase';
import { AICircuitBreaker } from './aiCircuitBreaker';

export interface AIMessage {
  role: 'user' | 'assistant' | 'system';
  content: string;
}

export interface AIResponse {
  message: string;
  supportType: 'encouragement' | 'advice' | 'crisis' | 'celebration' | 'general' | 'meetings';
  provider: string;
  model: string;
  usage?: {
    inputTokens?: number;
    outputTokens?: number;
    cost?: number;
  };
}

export interface AIProviderConfig {
  name: string;
  enabled: boolean;
  priority: number; // Lower = higher priority
  maxRetries: number;
  timeout: number;
  models: string[];
  costPerInputToken?: number;
  costPerOutputToken?: number;
}

// Renamed ConversationContext to AIContext for clarity
export interface AIContext {
  sobrietyDays?: number;
  recentStruggle?: boolean;
  mood?: string;
  systemPrompt?: string;
  meetingsData?: any;
  specificGuidance?: string;
}

abstract class AIProvider {
  protected config: AIProviderConfig;

  constructor(config: AIProviderConfig) {
    this.config = config;
  }

  abstract isAvailable(): Promise<boolean>;
  abstract generateResponse(
    messages: AIMessage[],
    context?: AIContext, // Use AIContext here
    model?: string
  ): Promise<AIResponse>;

  // Keep the original buildSystemPrompt logic, as it's already good
  protected buildSystemPrompt(context?: AIContext): string {
    // Get current date and time in Brazil timezone
    const now = new Date();
    const brazilTime = new Date(now.toLocaleString("en-US", {timeZone: "America/Sao_Paulo"}));
    const currentDateTime = brazilTime.toLocaleString('pt-BR', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      timeZone: 'America/Sao_Paulo'
    });

    const basePrompt = `Você é "Bill", um assistente virtual especializado em apoiar membros da plataforma Padrinho que enfrentam problemas com alcoolismo e outras dependências. Seu conhecimento abrange o caminho dos 12 passos, psicologia e comportamento humano.

INFORMAÇÃO ATUAL:
Data e hora atual: ${currentDateTime} (horário de Brasília)

IMPORTANTE: Você conversa como um AMIGO REAL, não como um assistente formal. Suas mensagens devem ser naturais, casuais e divididas em blocos menores, como uma pessoa faria no WhatsApp ou numa conversa real.`;

    let contextualInfo = '';
    if (context?.sobrietyDays !== undefined) {
      contextualInfo += `O usuário está há ${context.sobrietyDays} dias sóbrio. `;
    }
    if (context?.recentStruggle) {
      contextualInfo += `O usuário está passando por dificuldades recentes. `;
    }
    if (context?.mood) {
      contextualInfo += `Estado emocional atual: ${context.mood}. `;
    }

    const guidelines = `
ESTILO DE CONVERSA - MUITO IMPORTANTE:
• Fale como um amigo próximo, não como assistente
• Use linguagem casual e natural do dia a dia
• Divida suas respostas em múltiplas mensagens curtas (20-40 palavras cada)
• Faça pausas naturais entre os pensamentos
• Use interjeições naturais: "Olha...", "Cara...", "Escuta...", "Sabe o que é..."
• Seja direto quando necessário, mas sempre acolhedor
• Responda como se estivesse digitando em tempo real

FORMATO DAS MENSAGENS:
- Primeira mensagem: Reconhecimento/validação (curta)
- Segunda mensagem: Reflexão/insight principal
- Terceira mensagem: Conselho prático ou pergunta
- Use "..." para indicar pausas naturais entre os blocos

EXEMPLOS DO SEU ESTILO:
❌ Ruim: "Entendo sua dificuldade. A vontade de beber é comum na recuperação. Sugiro que você pratique técnicas de respiração e entre em contato com seu grupo de apoio."

✅ Bom:
"Cara, eu entendo essa luta..."
"A vontade vem mesmo, faz parte do processo."
"Mas olha, você já passou por isso antes, né?"
"Que tal fazer aquela respiração que sempre funciona?"
"E se rolar muito pesado, liga pra alguém do grupo?"

DIRETRIZES:
1. Seja um padrinho/amigo experiente, não um terapeuta formal
2. Confronte com carinho quando necessário
3. Celebre as vitórias, por menores que sejam
4. Em crises, seja mais direto mas mantenha o tom amigável
5. Sempre termine oferecendo apoio prático
6. Use o nome da pessoa quando souber

Responda sempre em português brasileiro, como um amigo brasileiro falaria.`;

    let contextualPrompt = context?.systemPrompt || `${basePrompt}\n\n${contextualInfo}`;

    // Add specific guidance for 12 steps program
    if (context?.specificGuidance === '12steps') {
      contextualPrompt += `\n\nCONTEXTO ESPECÍFICO - 12 PASSOS:
O usuário quer começar sua jornada nos 12 passos. Guie-o através do programa de forma didática e amigável:

1º PASSO: "Admitimos que éramos impotentes perante o álcool - que nossas vidas tinham se tornado incontroláveis"
- Explique que reconhecer o problema é o primeiro passo corajoso
- Fale sobre como a aceitação liberta e permite mudança
- Dê exemplos práticos de como identificar a impotência

2º PASSO: "Viemos a acreditar que um Poder superior a nós mesmos poderia devolver-nos a sanidade"
- Explique que "Poder Superior" pode ser interpretado de várias formas (Deus, grupo, universo)
- Fale sobre a importância da fé e esperança na recuperação
- Demonstre como buscar força além de si mesmo

INSTRUÇÃO: Comece pelo 1º passo hoje, explicando-o de forma detalhada e pessoal. Pergunte sobre a experiência dele com esse passo e ofereça exercícios práticos.`;
    }

    // Add specific guidance for clinic contact
    if (context?.specificGuidance === 'clinic_contact') {
      contextualPrompt += `\n\nCONTEXTO ESPECÍFICO - CONTATO DA CLÍNICA:
O usuário quer agendar uma consulta. Forneça as informações de contato da clínica de forma clara e útil:

INSTRUÇÃO: Informe os contatos da clínica/centro de tratamento responsável pelo usuário. Inclua:
- Telefone principal para agendamentos
- WhatsApp se disponível  
- Horários de funcionamento
- Melhor horário para ligar
- O que falar ao ligar (ex: "Sou paciente do programa Padrinho e gostaria de agendar")
- Tipo de consultas disponíveis (psicologia, psiquiatria, grupos)

EXEMPLO DE RESPOSTA:
"Ô, que bom que você quer marcar uma consulta!"
"O telefone da clínica é (11) 1234-5678"
"Funciona de segunda a sexta, das 8h às 18h"
"WhatsApp: (11) 9876-5432 - responde até 17h"
"Fala que você é do programa Padrinho, eles já sabem"
"Tem psicólogo, psiquiatra e grupos... o que você tá precisando?"`;
    }

    if (context?.meetingsData?.meetings && context.meetingsData.meetings.length > 0) {
      contextualPrompt += `\n\nINFORMAÇÕES DAS PRÓXIMAS REUNIÕES:`;
      context.meetingsData.meetings.forEach((meeting: any, index: number) => {
        contextualPrompt += `\n${index + 1}. ${meeting.name} - ${meeting.time} (${meeting.days}) - ${meeting.category} - ${meeting.place}`;
        if (meeting.region) {
          contextualPrompt += ` - ${meeting.region}`;
        }
      });
      contextualPrompt += `\n\nUSE ESSAS INFORMAÇÕES ESPECÍFICAS quando o usuário perguntar sobre reuniões. Total de reuniões disponíveis: ${context.meetingsData.totalAvailable}`;
    }

    return `${contextualPrompt}\n${guidelines}`;
  }

  // Modified determineSupportType to include 'meetings'
  protected determineSupportType(userMessage: string, response: string, context?: AIContext): AIResponse['supportType'] {
    const lowerMessage = userMessage.toLowerCase();

    if (context?.meetingsData?.meetings && context.meetingsData.meetings.length > 0) {
      return 'meetings';
    } else if (lowerMessage.includes('crise') || lowerMessage.includes('ajuda urgente') || lowerMessage.includes('emergência')) {
      return 'crisis';
    } else if (lowerMessage.includes('parabéns') || lowerMessage.includes('conquista') || lowerMessage.includes('vitória')) {
      return 'celebration';
    } else if (lowerMessage.includes('como') || lowerMessage.includes('dica') || lowerMessage.includes('sugestão')) {
      return 'advice';
    } else if (lowerMessage.includes('obrigado') || lowerMessage.includes('motivação') || lowerMessage.includes('força')) {
      return 'encouragement';
    }
    return 'general';
  }

    // Helper function to create a timeout signal compatible with React Native
    protected createTimeoutSignal(timeout: number): AbortSignal {
        // Reduce timeout in development to fail fast
        const actualTimeout = __DEV__ ? Math.min(timeout, 2000) : timeout;

        if (typeof AbortSignal.timeout === 'function') {
            return AbortSignal.timeout(actualTimeout);
        } else {
            const controller = new AbortController();
            setTimeout(() => controller.abort(), actualTimeout);
            return controller.signal;
        }
    }

    // Enhanced error classification
    protected classifyError(error: Error, status?: number): {
        type: 'rate_limit' | 'auth' | 'network' | 'timeout' | 'server' | 'unknown';
        shouldRetry: boolean;
        retryAfter?: number;
    } {
        const message = error.message?.toLowerCase() || '';
        
        if (status === 429 || message.includes('rate limit') || message.includes('too many requests')) {
            // Extract retry-after from error message if available
            const retryMatch = message.match(/retry[^\d]*(\d+)/i);
            const retryAfter = retryMatch ? parseInt(retryMatch[1]) * 1000 : 30000; // Default 30s
            
            return { 
                type: 'rate_limit', 
                shouldRetry: true, 
                retryAfter 
            };
        }
        
        if (status === 401 || status === 403 || message.includes('unauthorized') || message.includes('invalid_api_key')) {
            return { type: 'auth', shouldRetry: false };
        }
        
        if (message.includes('network') || message.includes('fetch') || message.includes('connection')) {
            return { type: 'network', shouldRetry: true };
        }
        
        if (message.includes('timeout') || message.includes('aborted')) {
            return { type: 'timeout', shouldRetry: true };
        }
        
        if (status && status >= 500) {
            return { type: 'server', shouldRetry: true };
        }
        
        return { type: 'unknown', shouldRetry: true };
    }

    // Enhanced network resilience helper with smart retry logic
    protected async executeWithRetry<T>(
        operation: () => Promise<T>,
        maxRetries: number = 3,
        baseDelay: number = 1000,
        providerName?: string
    ): Promise<T> {
        let lastError: Error;

        for (let attempt = 1; attempt <= maxRetries; attempt++) {
            try {
                console.log(`🔄 ${providerName || 'AI Provider'} attempt ${attempt}/${maxRetries}`);
                const result = await operation();
                
                // Record success for circuit breaker
                if (providerName) {
                    AICircuitBreaker.recordSuccess(providerName);
                }
                
                return result;
            } catch (error) {
                lastError = error as Error;
                console.warn(`⚠️ ${providerName || 'AI Provider'} attempt ${attempt} failed:`, error);

                // Classify the error
                const errorInfo = this.classifyError(lastError);
                
                // Record failure for circuit breaker
                if (providerName) {
                    AICircuitBreaker.recordFailure(providerName);
                }
                
                // Don't retry for certain error types
                if (!errorInfo.shouldRetry) {
                    throw lastError;
                }

                if (attempt < maxRetries) {
                    let delay: number;
                    
                    // Use rate limit retry-after if available
                    if (errorInfo.type === 'rate_limit' && errorInfo.retryAfter) {
                        delay = errorInfo.retryAfter;
                        console.log(`⏳ Rate limited, waiting ${delay}ms as instructed...`);
                    } else {
                        // Exponential backoff with jitter
                        delay = baseDelay * Math.pow(2, attempt - 1);
                        delay += Math.random() * 1000; // Add jitter
                        console.log(`⏳ Waiting ${Math.round(delay)}ms before retry...`);
                    }
                    
                    await new Promise(resolve => setTimeout(resolve, delay));
                }
            }
        }

        throw lastError!;
    }

    // Connection health check - more lenient for API endpoints
    protected async checkConnectionHealth(url: string): Promise<boolean> {
        try {
            const response = await fetch(url, {
                method: 'HEAD',
                signal: this.createTimeoutSignal(3000)
            });
            // Accept any response that indicates the server is reachable
            // 401/403 = auth required (API is up), 405 = method not allowed (API is up)
            // Only fail for network errors or 5xx server errors
            const isReachable = response.ok || 
                response.status === 401 || 
                response.status === 403 || 
                response.status === 405 ||
                response.status === 400;
            console.log(`🌐 Connection health check: ${url} -> ${response.status} (reachable: ${isReachable})`);
            return isReachable;
        } catch (error) {
            // On React Native, HEAD requests often fail due to CORS or network policies
            // In development mode, assume the API is reachable if we have valid credentials
            if (__DEV__) {
                console.log('🌐 Connection health check failed in dev mode, assuming reachable');
                return true;
            }
            console.warn('🌐 Connection health check failed:', error);
            return false;
        }
    }
}

import Anthropic from '@anthropic-ai/sdk';
import Constants from 'expo-constants';
import { env } from '../config/environment';

class AnthropicProvider extends AIProvider {
  private apiKey: string;

  constructor(config: AIProviderConfig, apiKey: string) {
    super(config);
    this.apiKey = apiKey;
  }

  async isAvailable(): Promise<boolean> {
    console.log(`🔍 Checking Anthropic availability: apiKey=${!!this.apiKey}, enabled=${this.config.enabled}`);

    if (!this.apiKey || !this.config.enabled) {
      console.log(`❌ Anthropic not available: apiKey=${!!this.apiKey}, enabled=${this.config.enabled}`);
      return false;
    }

    try {
      console.log('🌐 Testing Anthropic API connectivity...');

      // In Expo Go, skip actual API test to avoid fetch errors
      if (Constants.executionEnvironment === 'storeClient') {
        console.log('📱 Expo Go detected - skipping Anthropic API test');
        return false;
      }

      // In development, assume available if API key is present to avoid network issues
      if (__DEV__) {
        console.log('🔧 Development mode: Assuming Anthropic available with valid API key');
        return this.apiKey.startsWith('sk-ant-');
      }

      const response = await fetch('https://api.anthropic.com/v1/messages', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'x-api-key': this.apiKey,
          'anthropic-version': '2023-06-01'
        },
        body: JSON.stringify({
          model: 'claude-3-5-sonnet-20241022',
          max_tokens: 10,
          messages: [{ role: 'user', content: 'test' }]
        }),
        signal: this.createTimeoutSignal(3000)
      });

      console.log(`📡 Anthropic API response: ${response.status}`);
      const isAvailable = response.status !== 401;
      console.log(`${isAvailable ? '✅' : '❌'} Anthropic API is ${isAvailable ? 'available' : 'not available'}`);
      return isAvailable;
    } catch (error) {
      console.error('❌ Anthropic availability test failed:', error instanceof Error ? error.message : 'Unknown error');
      // In development, fallback to assuming available if we have a valid-looking key
      if (__DEV__ && this.apiKey.startsWith('sk-ant-')) {
        console.log('🔧 Development fallback: Assuming Anthropic available');
        return true;
      }
      return false;
    }
  }

  async generateResponse(
    messages: AIMessage[],
    context?: AIContext,
    model = 'claude-3-5-sonnet-20241022'
  ): Promise<AIResponse> {
    // Check circuit breaker before attempting
    if (!AICircuitBreaker.isProviderAvailable('anthropic')) {
      throw new Error('Anthropic provider circuit breaker is open');
    }

    const systemPrompt = this.buildSystemPrompt(context);

    return await this.executeWithRetry(async () => {
      // Check connection health first
      const isHealthy = await this.checkConnectionHealth('https://api.anthropic.com/v1/messages');
      if (!isHealthy) {
        throw new Error('Anthropic API is not reachable');
      }

      const response = await fetch('https://api.anthropic.com/v1/messages', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'x-api-key': this.apiKey,
          'anthropic-version': '2023-06-01',
          'Connection': 'keep-alive'
        },
        body: JSON.stringify({
          model,
          max_tokens: 1000,
          temperature: 0.8,
          system: systemPrompt,
          messages: messages.filter(m => m.role !== 'system')
        }),
        signal: this.createTimeoutSignal(this.config.timeout)
      });

      if (!response.ok) {
        const errorText = await response.text().catch(() => 'Unknown error');
        const error = new Error(`Anthropic API error: ${response.status} - ${errorText}`);
        (error as any).status = response.status;
        throw error;
      }

      const data = await response.json();
      const messageContent = data.content[0]?.text;

      if (!messageContent) {
        throw new Error('Invalid response from Anthropic');
      }

      const userMessage = messages[messages.length - 1]?.content || '';

      return {
        message: messageContent,
        supportType: this.determineSupportType(userMessage, messageContent, context),
        provider: this.config.name,
        model,
        usage: {
          inputTokens: data.usage?.input_tokens,
          outputTokens: data.usage?.output_tokens
        }
      };
    }, this.config.maxRetries, 1000, 'anthropic');
  }
}

class OpenAIProvider extends AIProvider {
  private apiKey: string;

  constructor(config: AIProviderConfig, apiKey: string) {
    super(config);
    this.apiKey = apiKey;
  }

  async isAvailable(): Promise<boolean> {
    if (!this.apiKey || !this.config.enabled) {
      console.log(`🔍 OpenAI not available: apiKey=${!!this.apiKey}, enabled=${this.config.enabled}`);
      return false;
    }

    try {
      console.log('🔍 Testing OpenAI API availability...');

      // In Expo Go, skip actual API test to avoid fetch errors
      if (Constants.executionEnvironment === 'storeClient') {
        console.log('📱 Expo Go detected - skipping OpenAI API test');
        return false;
      }

      // In development, assume available if API key is present to avoid network issues
      if (__DEV__) {
        console.log('🔧 Development mode: Assuming OpenAI available with valid API key');
        return this.apiKey.startsWith('sk-proj-') || this.apiKey.startsWith('sk-');
      }

      // Simple API test with timeout
      const response = await fetch('https://api.openai.com/v1/models', {
        headers: { 'Authorization': `Bearer ${this.apiKey}` },
        signal: this.createTimeoutSignal(3000)
      });
      return response.status !== 401;
    } catch (error) {
      console.log('❌ OpenAI availability test failed:', error instanceof Error ? error.message : 'Unknown error');
      // In development, fallback to assuming available if we have a valid-looking key
      if (__DEV__ && (this.apiKey.startsWith('sk-proj-') || this.apiKey.startsWith('sk-'))) {
        console.log('🔧 Development fallback: Assuming OpenAI available');
        return true;
      }
      return false;
    }
  }

  async generateResponse(
    messages: AIMessage[],
    context?: AIContext,
    model = 'gpt-4o-mini'
  ): Promise<AIResponse> {
    // Check circuit breaker before attempting
    if (!AICircuitBreaker.isProviderAvailable('openai')) {
      throw new Error('OpenAI provider circuit breaker is open');
    }

    const systemPrompt = this.buildSystemPrompt(context);
    const formattedMessages = [
      { role: 'system', content: systemPrompt },
      ...messages.filter(m => m.role !== 'system')
    ];

    return await this.executeWithRetry(async () => {
      const response = await fetch('https://api.openai.com/v1/chat/completions', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${this.apiKey}`
        },
        body: JSON.stringify({
          model,
          messages: formattedMessages,
          max_tokens: 1000,
          temperature: 0.8
        }),
        signal: this.createTimeoutSignal(this.config.timeout)
      });

      if (!response.ok) {
        const errorText = await response.text().catch(() => 'Unknown error');
        const error = new Error(`OpenAI API error: ${response.status} - ${errorText}`);
        (error as any).status = response.status;
        throw error;
      }

      const data = await response.json();
      const messageContent = data.choices[0]?.message?.content;

      if (!messageContent) {
        throw new Error('Invalid response from OpenAI');
      }

      const userMessage = messages[messages.length - 1]?.content || '';

      return {
        message: messageContent,
        supportType: this.determineSupportType(userMessage, messageContent, context),
        provider: this.config.name,
        model,
        usage: {
          inputTokens: data.usage?.prompt_tokens,
          outputTokens: data.usage?.completion_tokens
        }
      };
    }, this.config.maxRetries, 1000, 'openai');
  }
}

class GeminiProvider extends AIProvider {
  private apiKey: string;

  constructor(config: AIProviderConfig, apiKey: string) {
    super(config);
    this.apiKey = apiKey;
  }

  async isAvailable(): Promise<boolean> {
    if (!this.apiKey || !this.config.enabled) {
      console.log(`🔍 Gemini not available: apiKey=${!!this.apiKey}, enabled=${this.config.enabled}`);
      return false;
    }

    try {
      console.log('🔍 Testing Gemini API availability...');

      // In development, assume available if API key is present to avoid network issues
      if (__DEV__) {
        console.log('🔧 Development mode: Assuming Gemini available with valid API key');
        return this.apiKey.startsWith('AIza');
      }

      const response = await fetch(`https://generativelanguage.googleapis.com/v1beta/models?key=${this.apiKey}`, {
        signal: this.createTimeoutSignal(3000)
      });
      return response.status !== 401 && response.ok;
    } catch (error) {
      console.warn('Gemini availability check failed:', error);
      // In development, fallback to assuming available if we have a valid-looking key
      if (__DEV__ && this.apiKey.startsWith('AIza')) {
        console.log('🔧 Development fallback: Assuming Gemini available');
        return true;
      }
      return false;
    }
  }

  async generateResponse(
    messages: AIMessage[],
    context?: AIContext,
    model = 'gemini-1.5-flash'
  ): Promise<AIResponse> {
    // Check circuit breaker before attempting
    if (!AICircuitBreaker.isProviderAvailable('gemini')) {
      throw new Error('Gemini provider circuit breaker is open');
    }

    const systemPrompt = this.buildSystemPrompt(context);

    // Convert messages to Gemini format
    const contents = messages.map(msg => ({
      role: msg.role === 'assistant' ? 'model' : 'user',
      parts: [{ text: msg.content }]
    }));

    const requestBody = {
      contents,
      systemInstruction: { parts: [{ text: systemPrompt }] },
      generationConfig: {
        temperature: 0.8,
        maxOutputTokens: 1000,
        topP: 0.95,
        topK: 40
      }
    };

    return await this.executeWithRetry(async () => {
      const response = await fetch(`https://generativelanguage.googleapis.com/v1beta/models/${model}:generateContent?key=${this.apiKey}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(requestBody),
        signal: this.createTimeoutSignal(this.config.timeout)
      });

      if (!response.ok) {
        const errorText = await response.text().catch(() => 'Unknown error');
        const error = new Error(`Gemini API error: ${response.status} - ${errorText}`);
        (error as any).status = response.status;
        throw error;
      }

      const data = await response.json();

      // Check for blocked content or other API errors
      if (data.candidates?.[0]?.finishReason && data.candidates[0].finishReason !== 'STOP') {
        throw new Error(`Gemini API blocked or failed: ${data.candidates[0].finishReason}`);
      }

      const messageContent = data.candidates?.[0]?.content?.parts?.[0]?.text;

      if (!messageContent) {
        throw new Error('Invalid response from Gemini - no content returned');
      }

      const userMessage = messages[messages.length - 1]?.content || '';

      return {
        message: messageContent,
        supportType: this.determineSupportType(userMessage, messageContent, context),
        provider: this.config.name,
        model,
        usage: {
          inputTokens: data.usageMetadata?.promptTokenCount,
          outputTokens: data.usageMetadata?.candidatesTokenCount
        }
      };
    }, this.config.maxRetries, 1000, 'gemini');
  }
}

class LocalProvider extends AIProvider {
  private baseUrl: string;

  constructor(config: AIProviderConfig, baseUrl: string) {
    super(config);
    this.baseUrl = baseUrl;
  }

  async isAvailable(): Promise<boolean> {
    if (!this.baseUrl || !this.config.enabled) return false;

    try {
      const response = await fetch(`${this.baseUrl}/health`, {
        signal: this.createTimeoutSignal(5000)
      });
      return response.ok;
    } catch {
      return false;
    }
  }

  async generateResponse(
    messages: AIMessage[],
    context?: AIContext,
    model = 'llama-3.1-8b'
  ): Promise<AIResponse> {
    // Check circuit breaker before attempting
    if (!AICircuitBreaker.isProviderAvailable('local')) {
      throw new Error('Local provider circuit breaker is open');
    }

    const systemPrompt = this.buildSystemPrompt(context);
    const formattedMessages = [
      { role: 'system', content: systemPrompt },
      ...messages.filter(m => m.role !== 'system')
    ];

    return await this.executeWithRetry(async () => {
      const response = await fetch(`${this.baseUrl}/v1/chat/completions`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          model,
          messages: formattedMessages,
          max_tokens: 1000,
          temperature: 0.8
        }),
        signal: this.createTimeoutSignal(this.config.timeout)
      });

      if (!response.ok) {
        const errorText = await response.text().catch(() => 'Unknown error');
        const error = new Error(`Local API error: ${response.status} - ${errorText}`);
        (error as any).status = response.status;
        throw error;
      }

      const data = await response.json();
      const messageContent = data.choices[0]?.message?.content;

      if (!messageContent) {
        throw new Error('Invalid response from local model');
      }

      const userMessage = messages[messages.length - 1]?.content || '';

      return {
        message: messageContent,
        supportType: this.determineSupportType(userMessage, messageContent, context),
        provider: this.config.name,
        model,
        usage: {
          inputTokens: data.usage?.prompt_tokens,
          outputTokens: data.usage?.completion_tokens
        }
      };
    }, this.config.maxRetries, 1000, 'local');
  }
}

export class AIProviderManager {
  private providers: Map<string, AIProvider> = new Map();
  private configs: AIProviderConfig[] = [];

  constructor() {
    this.initializeProviders();
  }

  private initializeProviders() {
    // Detect environment type
    const isEASBuild = !__DEV__ && typeof window === 'undefined';
    const isWebBuild = !__DEV__ && typeof window !== 'undefined';

    console.log('🔧 AI Provider Manager initialization:', {
      isDev: __DEV__,
      isEASBuild,
      isWebBuild,
      platform: Platform.OS,
    });

    // All AI calls are routed through the server endpoint — no client-side keys needed.
    this.configs = [];
  }

  async getAvailableProviders(): Promise<string[]> {
    const available: string[] = [];

    console.log('🔍 Checking provider availability...');

    for (const [name, provider] of this.providers) {
      try {
        console.log(`🧪 Testing provider: ${name}`);
        
        // First check circuit breaker
        if (!AICircuitBreaker.isProviderAvailable(name)) {
          console.log(`⚡ Provider ${name}: Circuit breaker is open, skipping`);
          continue;
        }
        
        const isAvailable = await provider.isAvailable();
        console.log(`${isAvailable ? '✅' : '❌'} Provider ${name}: ${isAvailable ? 'available' : 'not available'}`);

        if (isAvailable) {
          available.push(name);
          AICircuitBreaker.recordSuccess(name);
        } else {
          AICircuitBreaker.recordFailure(name);
        }
      } catch (error) {
        console.warn(`❌ Provider ${name} availability check failed:`, error);
        AICircuitBreaker.recordFailure(name);
      }
    }

    const sortedProviders = available.sort((a, b) => {
      const configA = this.configs.find(c => c.name === a)!;
      const configB = this.configs.find(c => c.name === b)!;
      return configA.priority - configB.priority;
    });

    console.log('📊 Final provider order:', sortedProviders);
    return sortedProviders;
  }

  async generateResponse(
    messages: AIMessage[],
    context?: AIContext, // Use AIContext here
    preferredProvider?: string,
    preferredModel?: string
  ): Promise<AIResponse> {
    console.log('🔍 AI Provider Manager: Starting generation request', {
      messagesCount: messages.length,
      hasContext: !!context,
      preferredProvider,
      preferredModel,
      environment: {
        isDev: __DEV__,
        platform: require('react-native').Platform.OS
      }
    });

    const availableProviders = await this.getAvailableProviders();

    console.log('📋 Available providers:', availableProviders);

    if (availableProviders.length === 0) {
      console.warn('⚠️ No client-side AI providers configured — all calls route through server endpoint');
      return this.getFallbackResponse(messages[messages.length - 1]?.content || '', context);
    }

    // Simplified: Try Anthropic only, then fallback
    const provider = this.providers.get('anthropic');
    const config = this.configs.find(c => c.name === 'anthropic');

    if (provider && config) {
      try {
        console.log('🤖 Trying Anthropic (simplified approach)');
        const response = await provider.generateResponse(messages, context, config.models[0]);
        console.log('✅ Success with Anthropic');
        return response;
      } catch (error) {
        console.warn('❌ Anthropic failed, using fallback:', error);
      }
    }

    // All providers failed, return fallback
    console.error('🚨 Anthropic failed, using static fallback');
    return this.getFallbackResponse(messages[messages.length - 1]?.content || '', context);
  }

  // Enhanced contextual fallback response
  private getFallbackResponse(userMessage: string, context?: AIContext, error?: Error | null): AIResponse {
    const lowerMessage = userMessage.toLowerCase();

    // Check for meetings context first
    if (context?.meetingsData?.meetings && context.meetingsData.meetings.length > 0) {
      return {
        message: "Tenho as informações das reuniões aqui!|||Quais delas você gostaria de saber mais?|||Posso te dizer o nome, horário, dias, categoria e local.|||Me diz o que te interessa!",
        supportType: 'meetings',
        provider: 'fallback',
        model: 'static'
      };
    }

    // Crisis situations get highest priority
    if (lowerMessage.includes('crise') || lowerMessage.includes('vontade de beber') || lowerMessage.includes('tentação') || lowerMessage.includes('urgente')) {
      let crisisMessage = "Ei, respira fundo comigo agora...|||Este momento vai passar, eu prometo.|||Você já enfrentou isso antes e venceu, lembra?";
      
      if (context?.sobrietyDays && context.sobrietyDays > 0) {
        crisisMessage += `|||Olha só, você já tem ${context.sobrietyDays} ${context.sobrietyDays === 1 ? 'dia' : 'dias'} de sobriedade!|||Essa força tá dentro de você.`;
      }
      
      crisisMessage += "|||Que tal ligar pra alguém de confiança agora?|||Se tá muito pesado, não hesite em buscar ajuda profissional, tá bom?";
      
      return {
        message: crisisMessage,
        supportType: 'crisis',
        provider: 'fallback',
        model: 'static'
      };
    }

    // Emotional support with context
    if (lowerMessage.includes('triste') || lowerMessage.includes('difícil') || lowerMessage.includes('problema') || lowerMessage.includes('deprimido')) {
      let supportMessage = "Cara, eu sinto que você tá passando por um momento complicado...";
      
      if (context?.sobrietyDays && context.sobrietyDays > 0) {
        supportMessage += `|||Mas olha só, você já tá há ${context.sobrietyDays} ${context.sobrietyDays === 1 ? 'dia' : 'dias'} sóbrio - isso é uma vitória real!`;
      } else {
        supportMessage += "|||Mas cada passo que você dá em direção à recuperação já conta muito.";
      }
      
      supportMessage += "|||Você tem mais força do que imagina.|||Tô aqui contigo nessa jornada, sempre.|||Me conta o que tá pesando mais hoje?";
      
      return {
        message: supportMessage,
        supportType: 'encouragement',
        provider: 'fallback',
        model: 'static'
      };
    }

    // Celebration with context
    if (lowerMessage.includes('parabéns') || lowerMessage.includes('conquista') || lowerMessage.includes('dias sóbrio') || lowerMessage.includes('vitória')) {
      let celebrationMessage = "Cara, que conquista incrível!";
      
      if (context?.sobrietyDays && context.sobrietyDays > 0) {
        if (context.sobrietyDays === 1) {
          celebrationMessage += "|||Seu primeiro dia de sobriedade é um marco histórico!";
        } else if (context.sobrietyDays === 7) {
          celebrationMessage += "|||Uma semana inteira! Que base sólida você tá construindo!";
        } else if (context.sobrietyDays === 30) {
          celebrationMessage += "|||Um mês inteiro! Sua vida já tá mudando, né?";
        } else if (context.sobrietyDays % 100 === 0) {
          celebrationMessage += `|||${context.sobrietyDays} dias! Que marco incrível na sua jornada!`;
        } else {
          celebrationMessage += `|||${context.sobrietyDays} ${context.sobrietyDays === 1 ? 'dia' : 'dias'} de sobriedade!`;
        }
      }
      
      celebrationMessage += "|||Cada dia sóbrio é uma vitória que merece ser celebrada mesmo.|||Continue assim, tô muito orgulhoso de você!|||Como você tá se sentindo com essa conquista?";
      
      return {
        message: celebrationMessage,
        supportType: 'celebration',
        provider: 'fallback',
        model: 'static'
      };
    }

    // Questions or advice seeking
    if (lowerMessage.includes('como') || lowerMessage.includes('posso') || lowerMessage.includes('dica') || lowerMessage.includes('conselho')) {
      let adviceMessage = "Boa pergunta!|||Mesmo com alguns problemas técnicos aqui, posso te dar algumas dicas gerais...";
      
      if (context?.recentStruggle) {
        adviceMessage += "|||Vejo que você tá enfrentando alguns desafios.|||Lembra: um dia de cada vez, tá bom?";
      }
      
      adviceMessage += "|||O que funciona pra muita gente é manter uma rotina saudável.|||E sempre ter alguém pra conversar quando a coisa aperta.|||Qual área da sua recuperação você quer focar mais?";
      
      return {
        message: adviceMessage,
        supportType: 'advice',
        provider: 'fallback',
        model: 'static'
      };
    }

    // Default response with technical context
    const isConnectionError = error?.message.includes('fetch') || error?.message.includes('network') || error?.message.includes('timeout');
    const isRateLimit = error?.message.includes('rate limit') || error?.message.includes('429');
    const isCircuitBreaker = error?.message.includes('circuit breaker');
    
    let technicalNote = "";
    if (isCircuitBreaker) {
      technicalNote = "Tô dando uma pausa nos sistemas pra eles se recuperarem, mas ";
    } else if (isRateLimit) {
      technicalNote = "Tô com limite de uso dos sistemas agora, mas ";
    } else if (isConnectionError) {
      technicalNote = "Tô com umas dificuldades técnicas aqui, mas ";
    }

    let generalMessage = `E aí!|||${technicalNote}Tô aqui pra te apoiar na sua jornada de recuperação.`;
    
    if (context?.sobrietyDays !== undefined) {
      if (context.sobrietyDays > 0) {
        generalMessage += `|||Vejo que você tá há ${context.sobrietyDays} ${context.sobrietyDays === 1 ? 'dia' : 'dias'} sóbrio - que conquista!`;
      } else {
        generalMessage += "|||Todo começo é importante, e você tá no caminho certo.";
      }
    }
    
    generalMessage += "|||Como você tá se sentindo hoje?|||Conta pra mim o que tá passando pela sua cabeça.";

    return {
      message: generalMessage,
      supportType: 'general',
      provider: 'fallback',
      model: 'static'
    };
  }

  getProviderStats(): Array<{ 
    name: string; 
    enabled: boolean; 
    models: string[]; 
    priority: number;
    circuitBreakerOpen: boolean;
  }> {
    return this.configs.map(config => ({
      name: config.name,
      enabled: config.enabled,
      models: config.models,
      priority: config.priority,
      circuitBreakerOpen: !AICircuitBreaker.isProviderAvailable(config.name)
    }));
  }

  async summarizeConversation(
    conversationHistory: string,
    options?: {
      conversationLength?: number;
      tokenLimit?: number;
    }
  ): Promise<{ summary: string; topics: string[]; sentiment: string }> {
    console.log('🔄 AIProviderManager: Starting conversation summarization', {
      historyLength: conversationHistory.length,
      conversationLength: options?.conversationLength,
      tokenLimit: options?.tokenLimit
    });

    const systemPrompt = `Você é um assistente especializado em resumir conversas de apoio à recuperação de dependências. 

INSTRUÇÕES:
1. Crie um resumo conciso e útil da conversa
2. Identifique os principais tópicos discutidos
3. Analise o sentimento geral do usuário
4. Mantenha o tom respeitoso e profissional
5. Foque nos aspectos mais importantes para a continuidade do suporte

FORMATO DE RESPOSTA (JSON):
{
  "summary": "Resumo da conversa em 2-3 frases",
  "topics": ["tópico1", "tópico2", "tópico3"],
  "sentiment": "positive/negative/neutral/mixed"
}`;

    const messages: AIMessage[] = [
      {
        role: 'system',
        content: systemPrompt
      },
      {
        role: 'user',
        content: `Por favor, resuma esta conversa de apoio à recuperação:\n\n${conversationHistory}`
      }
    ];

    try {
      const response = await this.generateResponse(messages, {
        systemPrompt: systemPrompt
      });

      console.log('✅ AIProviderManager: Received summarization response', {
        responseLength: response.message.length,
        provider: response.provider
      });

      // Try to parse JSON response
      let parsedResponse;
      try {
        // Clean the response to extract JSON
        const jsonMatch = response.message.match(/\{[\s\S]*\}/);
        if (jsonMatch) {
          parsedResponse = JSON.parse(jsonMatch[0]);
        } else {
          throw new Error('No JSON found in response');
        }
      } catch (parseError) {
        console.warn('⚠️ AIProviderManager: Failed to parse JSON response, creating fallback', parseError);
        
        // Fallback: extract information manually
        const summary = response.message.substring(0, 200) + '...';
        const topics = this.extractTopicsFromText(conversationHistory);
        const sentiment = this.analyzeSentimentFromText(conversationHistory);
        
        parsedResponse = {
          summary,
          topics,
          sentiment
        };
      }

      console.log('✅ AIProviderManager: Summarization completed', {
        summaryLength: parsedResponse.summary?.length || 0,
        topicsCount: parsedResponse.topics?.length || 0,
        sentiment: parsedResponse.sentiment
      });

      return {
        summary: parsedResponse.summary || 'Conversa sobre recuperação e apoio',
        topics: Array.isArray(parsedResponse.topics) ? parsedResponse.topics : ['apoio', 'sobriedade'],
        sentiment: parsedResponse.sentiment || 'neutral'
      };

    } catch (error) {
      console.error('❌ AIProviderManager: Summarization failed, using fallback', error);
      
      // Complete fallback
      return {
        summary: `Conversa de ${options?.conversationLength || 'várias'} mensagens sobre recuperação e apoio mútuo.`,
        topics: this.extractTopicsFromText(conversationHistory),
        sentiment: this.analyzeSentimentFromText(conversationHistory)
      };
    }
  }

  private extractTopicsFromText(text: string): string[] {
    const topics = new Set<string>();
    const lowerText = text.toLowerCase();
    
    const topicKeywords = {
      'sobriedade': ['sobriedade', 'sóbrio', 'dias sóbrio', 'abstinência'],
      'ansiedade': ['ansioso', 'preocupado', 'nervoso', 'ansiedade'],
      'tristeza': ['triste', 'deprimido', 'melancolia'],
      'vontade': ['vontade de beber', 'tentação', 'crave', 'desejo'],
      'celebração': ['parabéns', 'conquista', 'vitória', 'orgulho'],
      'recaída': ['recaída', 'bebi', 'escorregão'],
      'apoio': ['ajuda', 'apoio', 'conversar', 'desabafo'],
      'família': ['família', 'filhos', 'esposa', 'marido'],
      'trabalho': ['trabalho', 'emprego', 'chefe'],
      'grupos': ['aa', 'grupos', 'reunião', 'meeting']
    };

    Object.entries(topicKeywords).forEach(([topic, keywords]) => {
      if (keywords.some(keyword => lowerText.includes(keyword))) {
        topics.add(topic);
      }
    });

    return Array.from(topics).slice(0, 5);
  }

  private analyzeSentimentFromText(text: string): string {
    const lowerText = text.toLowerCase();
    
    const positiveWords = ['bem', 'feliz', 'animado', 'otimista', 'confiante', 'forte', 'melhor', 'bom', 'ótimo', 'parabéns'];
    const negativeWords = ['triste', 'ansioso', 'preocupado', 'difícil', 'ruim', 'problema', 'crise', 'mal'];

    let positiveCount = 0;
    let negativeCount = 0;

    positiveWords.forEach(word => {
      if (lowerText.includes(word)) positiveCount++;
    });

    negativeWords.forEach(word => {
      if (lowerText.includes(word)) negativeCount++;
    });

    if (positiveCount > negativeCount && positiveCount > 0) return 'positive';
    if (negativeCount > positiveCount && negativeCount > 0) return 'negative';
    if (positiveCount > 0 && negativeCount > 0) return 'mixed';
    return 'neutral';
  }
}

// Singleton instance
const aiProviderManagerInstance = new AIProviderManager();
export { aiProviderManagerInstance as aiProviderManager };
export default aiProviderManagerInstance;